import { listVendorTermsOptions } from '@diagon-alley/common';
import React, { useCallback, useMemo, useState } from 'react';
import { Group, Modal, Stack, Text, Tooltip } from '@mantine/core';
import { Check, Plus, UploadSimple, Warning, X } from '@phosphor-icons/react';

import {
    uploadEntityAttributeValues,
    upsertAttributeValue,
    patchCoreField,
    type MasterEntityType,
    type MasterTableColumnMeta,
} from '@api/entity-attributes';
import DataSheet, {
    DataSheetPagination,
    dataSheetTokens,
    type DataSheetColumn,
} from '@components/ui/DataSheet';

import useApp from '@providers/App/useApp';

import AddColumnModal from './AddColumnModal';
import {
    bulkUpsertWarehouses,
    WAREHOUSE_UPSERT_CSV_COLUMNS,
    WAREHOUSE_UPSERT_REQUIRED_COLUMNS,
} from '@api/warehouses';
import {
    bulkUpsertMaterials,
    MATERIAL_UPSERT_CSV_COLUMNS,
    MATERIAL_UPSERT_REQUIRED_COLUMNS,
} from '@api/materials';
import {
    bulkUpsertProducts,
    PRODUCT_UPSERT_CSV_COLUMNS,
    PRODUCT_UPSERT_REQUIRED_COLUMNS,
} from '@api/products';

import AddMaterialModal from './AddMaterialModal';
import AddProductModal from './AddProductModal';
import {
    bulkUpsertVendorMappings,
    bulkUpsertVendorMaterialMappings,
    deleteVendorMaterialMapping,
    listVendors,
    VENDOR_MAPPING_CSV_COLUMNS,
    VENDOR_MAPPING_CSV_REQUIRED_COLUMNS,
    VENDOR_MATERIAL_MAPPING_CSV_COLUMNS,
    VENDOR_MATERIAL_MAPPING_CSV_REQUIRED_COLUMNS,
    type VendorListItem,
    type VendorPaymentTermsConfig,
    uploadVendorPrices,
    VENDOR_PRICE_CSV_COLUMNS,
    VENDOR_PRICE_CSV_REQUIRED_COLUMNS,
} from '@api/vendors';
import { useQuery } from '@tanstack/react-query';
import { readApiError } from '@utils/apiError';

import AddVendorMappingModal from './AddVendorMappingModal';
import AddVendorPriceModal from './AddVendorPriceModal';
import AddVendorModal from './AddVendorModal';
import { PriceHistoryModal } from './PriceHistoryModal';
import { VendorTermsModal } from './VendorTermsModal';
import BulkUploadModal from './BulkUploadModal';
import EditableCell from './EditableCell';
import ImportCsvModal from './ImportCsvModal';
import {
    attributeExportHeader,
    attributeEntityType,
    isWideTextColumn,
    ENTITY_CONFIG,
    isMappingEntity,
    isMappingPriceColumn,
    ENTITY_EXPORT_KEY_HEADER,
    exportHeaderFor,
} from './entityMaster.constants';
import { useEntityMaster } from './useEntityMaster';

const tokens = dataSheetTokens;

/** A column is never narrower than the grid's default, never wider than this. */

const ANCHOR_SHORT: Record<string, string> = {
    ORDER_DATE: 'Order',
    READINESS: 'Ready',
    DISPATCH: 'Dispatch',
    ARRIVAL: 'Arrival',
    DELIVERY: 'Delivery',
    FIXED: 'Fixed',
};

/** One-line human summary of a vendor's terms config for the grid cell. */
function summarizeVendorTerms(
    cfg: VendorPaymentTermsConfig | null | undefined,
): string | null {
    if (!cfg) return null;
    if (cfg.financing) {
        return `${cfg.financing.creditDays}d credit · ${cfg.financing.premiumPct}% premium`;
    }
    const fmt = (t: { percentage: number; label: string; type: string; anchor: string; offsetDays?: number }) => {
        const offset = t.offsetDays
            ? t.offsetDays > 0
                ? `+${t.offsetDays}`
                : `−${Math.abs(t.offsetDays)}`
            : '';
        return `${t.percentage}% ${(t.label || t.type).toLowerCase()} @ ${ANCHOR_SHORT[t.anchor] ?? t.anchor}${offset}`;
    };
    const options = listVendorTermsOptions(cfg);
    if (options.length === 0) return null;
    // One set reads as the deal itself; several read as the choice they are.
    if (options.length === 1) return options[0].tranches.map(fmt).join(' · ');
    return `${options.length} sets: ${options.map((o) => o.name).join(', ')}`;
}

interface EntityMasterTabProps {
    entityType: MasterEntityType;
}

interface PendingChange {
    entityId: string;
    entityKey: string;
    colKey: string;
    colType: MasterTableColumnMeta['type'];
    attributeId?: string;
    newValue: string;
}

/** Token-styled toolbar button — matches the DataSheet design system */
const ToolbarButton: React.FC<{
    onClick: () => void;
    active?: boolean;
    disabled?: boolean;
    children: React.ReactNode;
    icon?: React.ReactNode;
}> = ({ onClick, active, disabled, children, icon }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '3px 10px',
            fontSize: 11,
            fontWeight: 500,
            color: active
                ? tokens.colors.surface
                : disabled
                  ? tokens.colors.textSecondary
                  : tokens.colors.textPrimary,
            backgroundColor: active
                ? tokens.colors.activeTab
                : tokens.colors.surface,
            border: `1px solid ${active ? tokens.colors.activeTab : tokens.colors.border}`,
            borderRadius: 4,
            cursor: disabled ? 'default' : 'pointer',
            height: tokens.dimensions.controlHeight,
            transition: 'all 0.15s ease',
            opacity: disabled ? 0.6 : 1,
        }}
    >
        {icon}
        {children}
    </button>
);

/** Small icon-only toolbar button */
const IconButton: React.FC<{
    onClick: () => void;
    label: string;
    disabled?: boolean;
    color?: 'default' | 'blue' | 'red';
    children: React.ReactNode;
}> = ({ onClick, label, disabled, color = 'default', children }) => {
    const bgMap = {
        default: tokens.colors.surface,
        blue: tokens.colors.activeTab,
        red: tokens.colors.surface,
    };
    const colorMap = {
        default: tokens.colors.textPrimary,
        blue: tokens.colors.surface,
        red: 'var(--mantine-color-red-6)',
    };
    return (
        <Tooltip label={label} withArrow>
            <button
                type="button"
                onClick={onClick}
                disabled={disabled}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: tokens.dimensions.controlHeight,
                    height: tokens.dimensions.controlHeight,
                    padding: 0,
                    backgroundColor: bgMap[color],
                    color: colorMap[color],
                    border: `1px solid ${color === 'blue' ? tokens.colors.activeTab : tokens.colors.border}`,
                    borderRadius: 4,
                    cursor: disabled ? 'default' : 'pointer',
                    transition: 'all 0.15s ease',
                    opacity: disabled ? 0.5 : 1,
                }}
            >
                {children}
            </button>
        </Tooltip>
    );
};

const EntityMasterTab: React.FC<EntityMasterTabProps> = ({ entityType }) => {
    const config = ENTITY_CONFIG[entityType];
    const {
        rows,
        columns: apiColumns,
        total,
        page,
        pageSize,
        isLoading,
        search,
        setSearch,
        setPage,
        setPageSize,
        reload,
        filterConfigs,
        addColumn,
        fetchAll,
    } = useEntityMaster(entityType);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    const { currentUser } = useApp();

    // Creating master data is ADMIN-only; the backend enforces it too. Hiding the
    // control keeps consultants and vendors from hitting a 403 they can't act on.
    const isAdmin = currentUser?.role === 'ADMIN';
    const canAddProduct = entityType === 'PRODUCT' && isAdmin;
    const canAddMaterial = entityType === 'MATERIAL' && isAdmin;
    /*
     * Vendors get no "Import CSV" alongside this. Creating vendors in bulk from a
     * spreadsheet is how the catalogue filled up with placeholder types and blank
     * addresses in the first place; one at a time, through a form that insists on a
     * real type, is the point. Bulk update of vendors that already exist is a
     * different job and keeps its toolbar button.
     */
    const canAddVendor = entityType === 'VENDOR' && isAdmin;
    // Both mapping tables take a row from the form or a file of them; the importers upsert.
    const canAddMapping = isMappingEntity(entityType) && isAdmin;
    /*
     * Material mappings can be removed, one at a time. A mapping is addressed by its
     * material + vendor pair, so "change the vendor" is really "remove this one, add that
     * one" - and until now the grid offered no remove at all, which left a wrong vendor
     * stuck. Product mappings have no row id to name, so they wait.
     */
    const canRemoveMapping = entityType === 'VENDOR_MATERIAL_MAPPING' && isAdmin;
    /*
     * Warehouses get Import CSV but no "Add warehouse" button beside it. A warehouse is
     * created by onboarding a channel or a WMS facility, which wires up far more than a
     * row -- the importer exists to EDIT the network in bulk, and can add a row when one
     * is genuinely missing, without implying a form is the way to grow the network.
     */
    const canImportWarehouses = entityType === 'WAREHOUSE' && isAdmin;
    const canImportPrices = entityType === 'VENDOR_PRICE' && isAdmin;

    const [editMode, setEditMode] = useState(false);
    const [editConfirmOpen, setEditConfirmOpen] = useState(false);
    const [addColumnOpen, setAddColumnOpen] = useState(false);
    /*
     * Which long-text cell has its editor open, as `entityId::colKey` — at most one.
     * Owned here rather than inside each cell so that opening one closes any other by
     * construction, the same way `openFilterKey` drives the column-filter popover.
     */
    const [openEditorKey, setOpenEditorKey] = useState<string | null>(null);
    const [addProductOpen, setAddProductOpen] = useState(false);
    const [addMaterialOpen, setAddMaterialOpen] = useState(false);
    const [addVendorOpen, setAddVendorOpen] = useState(false);
    const [termsVendor, setTermsVendor] = useState<VendorListItem | null>(null);
    /** The mapping row waiting for a confirmed remove, by its `_rowId`. */
    const [removeTarget, setRemoveTarget] = useState<Record<string, unknown> | null>(null);
    const [removing, setRemoving] = useState(false);
    const [removeError, setRemoveError] = useState<string | null>(null);

    const handleRemoveMapping = async () => {
        const rowId = removeTarget?._rowId;
        if (typeof rowId !== 'string') return;
        setRemoving(true);
        setRemoveError(null);
        try {
            await deleteVendorMaterialMapping(rowId);
            setRemoveTarget(null);
            reload();
        } catch (err) {
            setRemoveError(readApiError(err, 'Could not remove the mapping. Please try again.'));
        } finally {
            setRemoving(false);
        }
    };
    // The vendor mapping row whose price timeline is open.
    const [historyRow, setHistoryRow] = useState<Record<string, unknown> | null>(null);

    // Payment terms live on the vendors table jsonb, not in the attribute
    // system, so the VENDOR grid fetches them separately and joins by id.
    const { data: vendorsWithTerms, refetch: refetchVendorTerms } = useQuery(
        ['entity-master-vendor-terms'],
        listVendors,
        { enabled: entityType === 'VENDOR' },
    );
    const vendorsById = useMemo(
        () => new Map((vendorsWithTerms ?? []).map((v) => [v.vendorId, v])),
        [vendorsWithTerms],
    );
    const [addMappingOpen, setAddMappingOpen] = useState(false);
    const [addPriceOpen, setAddPriceOpen] = useState(false);
    const [importCsvOpen, setImportCsvOpen] = useState(false);
    const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
    const [pendingChanges, setPendingChanges] = useState<
        Record<string, PendingChange>
    >({});
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const pendingCount = Object.keys(pendingChanges).length;

    // First row natural key as example for bulk upload modal
    const sampleEntityKey = rows[0]?._entityKey as string | undefined;

    const handleEditClick = () => {
        if (!editMode) setEditConfirmOpen(true);
    };

    const handleDiscard = () => {
        setPendingChanges({});
        setSaveError(null);
        setEditMode(false);
        // Leaving edit mode must not strand an open editor over a grid that is no
        // longer editable.
        setOpenEditorKey(null);
    };

    const handleSave = async () => {
        if (pendingCount === 0) {
            setEditMode(false);
            setOpenEditorKey(null);
            return;
        }
        setSaving(true);
        setSaveError(null);
        try {
            await Promise.all(
                Object.values(pendingChanges).map(async (change) => {
                    if (change.colType === 'ATTRIBUTE' && change.attributeId) {
                        await upsertAttributeValue(
                            change.attributeId,
                            change.entityKey,
                            change.newValue,
                        );
                    } else {
                        await patchCoreField(
                            entityType,
                            change.entityId,
                            change.colKey,
                            change.newValue,
                        );
                    }
                }),
            );
            setPendingChanges({});
            setEditMode(false);
            setOpenEditorKey(null);
            reload();
        } catch {
            setSaveError('Some changes could not be saved. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const buildColumns = useMemo((): DataSheetColumn<
        Record<string, unknown>
    >[] => {
        return apiColumns.map((col) => {
            // Prices are an admin's to change and the server refuses anyone else; the cell
            // is locked with the reason on hover rather than left looking editable.
            const isPriceLocked =
                editMode && !isAdmin && isMappingPriceColumn(entityType, col.key);
            const isEditable = editMode && col.editable && !isPriceLocked;

            const render = isEditable
                ? (value: unknown, row: Record<string, unknown>) => {
                      // Category caps only mean something on a retail store; every
                      // other warehouse shows the value read-only.
                      if (
                          col.type === 'CATEGORY_CAPACITY' &&
                          String(row.type ?? '').toUpperCase() !== 'RETAIL'
                      ) {
                          return value == null ? '' : String(value);
                      }
                      const entityId = row._id as string;
                      const entityKey =
                          (row._entityKey as string | undefined) ?? entityId;
                      const changeKey = `${entityId}::${col.key}`;
                      const pending = pendingChanges[changeKey];

                      const onPendingChange = (newValue: string) => {
                          setPendingChanges((prev) => ({
                              ...prev,
                              [changeKey]: {
                                  entityId,
                                  entityKey,
                                  colKey: col.key,
                                  colType: col.type,
                                  attributeId: col.attributeId,
                                  newValue,
                              },
                          }));
                      };

                      return (
                          <EditableCell
                              value={value == null ? null : String(value)}
                              pendingValue={pending?.newValue}
                              onPendingChange={onPendingChange}
                              // Long-text columns edit in a popover; everything
                              // else keeps the in-cell input unchanged.
                              wide={isWideTextColumn(entityType, col.key)}
                              isOpen={openEditorKey === changeKey}
                              onOpenChange={(open) =>
                                  setOpenEditorKey(open ? changeKey : null)
                              }
                          />
                      );
                  }
                : isPriceLocked
                  ? (value: unknown) => (
                        <Tooltip label="Admins only" withArrow>
                            <Text span c="dimmed" size="sm">
                                {value == null ? '' : String(value)}
                            </Text>
                        </Tooltip>
                    )
                  : undefined;

            // Every column fits its longest value on the page: a material called
            // Cylindrical_Tube_DATE_BITE10 at the grid's 150px default ran into the Code cell
            // beside it, and most of the catalogue reads that way.
            return { key: col.key, header: col.header, width: 'fit' as const, render };
        });
        // entityType is stable for a mounted tab (the sections remount on switch via
        // `key`), but it is read above and so belongs here.
    }, [apiColumns, editMode, pendingChanges, entityType, openEditorKey, isAdmin]);

    const columnsWithTerms = useMemo((): DataSheetColumn<
        Record<string, unknown>
    >[] => {
        if (entityType === 'VENDOR_PRODUCT_MAPPING' || entityType === 'VENDOR_MATERIAL_MAPPING' || entityType === 'VENDOR_PRICE') {
            // The timeline cell opens the detail view; the text stays what the export writes.
            const withHistory = buildColumns.map((col) =>
                col.key !== 'priceHistory'
                    ? col
                    : {
                          ...col,
                          render: (value: unknown, row: Record<string, unknown>) =>
                              value ? (
                                  <span
                                      onClick={() => setHistoryRow(row)}
                                      style={{ cursor: 'pointer', color: 'rgb(1, 119, 204)', fontWeight: 500 }}
                                  >
                                      {String(value)}
                                  </span>
                              ) : (
                                  <span style={{ color: tokens.colors.textSecondary }}>—</span>
                              ),
                      },
            );
            if (!canRemoveMapping) return withHistory;
            const removeColumn: DataSheetColumn<Record<string, unknown>> = {
                key: '_remove',
                header: '',
                width: 90,
                render: (_value, row) => (
                    <span
                        onClick={() => setRemoveTarget(row)}
                        style={{ cursor: 'pointer', color: tokens.colors.textSecondary, fontSize: 12 }}
                    >
                        Remove
                    </span>
                ),
            };
            return [...withHistory, removeColumn];
        }
        if (entityType !== 'VENDOR') return buildColumns;
        const termsColumn: DataSheetColumn<Record<string, unknown>> = {
            key: '_paymentTerms',
            header: 'Payment Terms',
            render: (_value, row) => {
                const vendor = vendorsById.get(row._id as string);
                if (!vendor) return <span style={{ color: tokens.colors.textSecondary }}>—</span>;
                const summary = summarizeVendorTerms(vendor.paymentTermsConfig);
                return (
                    <span
                        onClick={() => setTermsVendor(vendor)}
                        style={{
                            cursor: 'pointer',
                            color: summary ? 'rgb(1, 119, 204)' : tokens.colors.textSecondary,
                            fontWeight: summary ? 500 : 400,
                        }}
                    >
                        {summary ?? 'Set terms'}
                    </span>
                );
            },
        };
        return [...buildColumns, termsColumn];
    }, [buildColumns, entityType, vendorsById, canRemoveMapping]);

    /**
     * The columns an export writes: the row's natural key, then everything on screen.
     *
     * Built from `apiColumns` rather than the rendered columns on purpose. The rendered
     * set carries an `EditableCell` in every cell while edit mode is on, and for vendors
     * a `_paymentTerms` column that is a button, not data — neither belongs in a file.
     * Headers are the importer's names, not the grid's labels (see `exportHeaderFor`).
     *
     * The identity column is dropped when a visible column already carries the same
     * header: vendors and vendor mappings show their key as a column of their own, and
     * two identically-named columns in one CSV is a file no importer can read.
     *
     * `brand` closes the file, when the row carries one. It is the export's provenance —
     * the brand NAME rather than the merchant uuid, so that somebody who opens the wrong
     * `products.csv` can see it is the wrong one before uploading it, which a uuid would
     * never let them do. The importer refuses a file whose brand is not theirs.
     */
    const downloadColumns = useMemo((): DataSheetColumn<
        Record<string, unknown>
    >[] => {
        // Category caps are edited in the grid only: the warehouse importer refuses
        // columns it does not know, so writing them would break the round trip.
        const dataColumns = apiColumns
            .filter((col) => col.type !== 'CATEGORY_CAPACITY')
            .map((col) => ({
                key: col.key,
                header: col.header,
                // Custom attributes are namespaced; built-in columns are not. Without that an
                // attribute whose slug matches a core column writes a second column of the same
                // name, and the importer cannot tell which of the two a value belongs to.
                csvHeader:
                    col.type === 'ATTRIBUTE'
                        ? attributeExportHeader(col.key)
                        : exportHeaderFor(entityType, col.key),
            }));

        const keyHeader = ENTITY_EXPORT_KEY_HEADER[entityType];
        const withKey = dataColumns.some((col) => col.csvHeader === keyHeader)
            ? dataColumns
            : [
                  { key: '_entityKey', header: keyHeader, csvHeader: keyHeader },
                  ...dataColumns,
              ];

        // Only the entity types whose query selects it — today that is products, the one
        // with an importer to guard. Absent elsewhere, the column simply is not written.
        const hasBrand = rows.some((row) => row._brand != null);

        return hasBrand
            ? [...withKey, { key: '_brand', header: 'Brand', csvHeader: 'brand' }]
            : withKey;
    }, [apiColumns, entityType, rows]);

    /**
     * Rows for the download. DataSheet builds the CSV itself, so this only has to say
     * which rows — the escaping, the header line and the blob are all its own.
     */
    const handleFetchAllData = useCallback(
        async () => ({ rows: await fetchAll() }),
        [fetchAll],
    );

    const handleAddColumnConfirm = useCallback(
        async (name: string, showInFilter: boolean, csvFile: File | null) => {
            const attributeId = await addColumn(name, showInFilter);
            if (csvFile) {
                await uploadEntityAttributeValues(attributeId, csvFile);
                reload();
            }
        },
        [addColumn, reload],
    );

    return (
        <>
            <DataSheet
                data={rows}
                columns={columnsWithTerms}
                isLoading={isLoading}
                enableSearch
                searchPlaceholder={config.searchPlaceholder}
                searchValue={search}
                onSearchChange={setSearch}
                filters={filterConfigs.length > 0 ? filterConfigs : undefined}
                enableDownload
                allColumns={downloadColumns}
                onFetchAllData={handleFetchAllData}
                downloadFilename={`${entityType.toLowerCase()}s.csv`}
                getRowKey={(row, i) =>
                    (row._id as string | undefined) ?? String(i)
                }
                toolbarControls={
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                        }}
                    >
                        {!editMode && (
                            <>
                                <ToolbarButton
                                    onClick={handleEditClick}
                                    icon={
                                        <svg
                                            width={12}
                                            height={12}
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                        >
                                            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                                        </svg>
                                    }
                                >
                                    Edit
                                </ToolbarButton>
                                {attributeEntityType(entityType) && (
                                    <ToolbarButton
                                        onClick={() => setAddColumnOpen(true)}
                                        icon={<Plus size={12} />}
                                    >
                                        Add column
                                    </ToolbarButton>
                                )}
                                {canAddProduct && (
                                    <>
                                        <ToolbarButton
                                            onClick={() => setAddProductOpen(true)}
                                            icon={<Plus size={12} />}
                                        >
                                            Add product
                                        </ToolbarButton>
                                        <ToolbarButton
                                            onClick={() =>
                                                setImportCsvOpen(true)
                                            }
                                            icon={<UploadSimple size={12} />}
                                        >
                                            Import CSV
                                        </ToolbarButton>
                                    </>
                                )}
                                {canImportWarehouses && (
                <ImportCsvModal
                    opened={importCsvOpen}
                    onClose={() => setImportCsvOpen(false)}
                    onImported={reload}
                    entityNoun="warehouse"
                    columns={WAREHOUSE_UPSERT_CSV_COLUMNS}
                    requiredColumns={WAREHOUSE_UPSERT_REQUIRED_COLUMNS}
                    keyLabel="Warehouse ID"
                    upload={bulkUpsertWarehouses}
                    mode="upsert"
                />
            )}

            {canImportPrices && (
                <ImportCsvModal
                    opened={importCsvOpen}
                    onClose={() => setImportCsvOpen(false)}
                    onImported={reload}
                    entityNoun="price line"
                    columns={VENDOR_PRICE_CSV_COLUMNS}
                    requiredColumns={VENDOR_PRICE_CSV_REQUIRED_COLUMNS}
                    keyLabel="Item"
                    upload={uploadVendorPrices}
                    mode="upsert"
                />
            )}

            {canAddVendor && (
                                    <ToolbarButton
                                        onClick={() => setAddVendorOpen(true)}
                                        icon={<Plus size={12} />}
                                    >
                                        Add vendor
                                    </ToolbarButton>
                                )}
                                {canAddMapping && (
                                    <>
                                        <ToolbarButton
                                            onClick={() => setAddMappingOpen(true)}
                                            icon={<Plus size={12} />}
                                        >
                                            Add mapping
                                        </ToolbarButton>
                                        <ToolbarButton
                                            onClick={() => setImportCsvOpen(true)}
                                            icon={<UploadSimple size={12} />}
                                        >
                                            Import CSV
                                        </ToolbarButton>
                                    </>
                                )}
                                {canAddMaterial && (
                                    <>
                                        <ToolbarButton
                                            onClick={() => setAddMaterialOpen(true)}
                                            icon={<Plus size={12} />}
                                        >
                                            Add material
                                        </ToolbarButton>
                                        <ToolbarButton
                                            onClick={() => setImportCsvOpen(true)}
                                            icon={<UploadSimple size={12} />}
                                        >
                                            Import CSV
                                        </ToolbarButton>
                                    </>
                                )}
                                {canImportWarehouses && (
                                    <ToolbarButton
                                        onClick={() => setImportCsvOpen(true)}
                                        icon={<UploadSimple size={12} />}
                                    >
                                        Import CSV
                                    </ToolbarButton>
                                )}
                                {canImportPrices && (
                                    <>
                                        <ToolbarButton
                                            onClick={() => setAddPriceOpen(true)}
                                            icon={<Plus size={12} />}
                                        >
                                            Add price
                                        </ToolbarButton>
                                        <ToolbarButton
                                            onClick={() => setImportCsvOpen(true)}
                                            icon={<UploadSimple size={12} />}
                                        >
                                            Import price list
                                        </ToolbarButton>
                                    </>
                                )}
                            </>
                        )}
                        {editMode && (
                            <>
                                {!isMappingEntity(entityType) && (
                                    <IconButton
                                        onClick={() => setBulkUploadOpen(true)}
                                        label="Bulk upload CSV"
                                    >
                                        <UploadSimple size={13} />
                                    </IconButton>
                                )}
                                <IconButton
                                    onClick={handleDiscard}
                                    label="Discard changes"
                                    color="red"
                                >
                                    <X size={13} />
                                </IconButton>
                                <IconButton
                                    onClick={handleSave}
                                    label={
                                        pendingCount > 0
                                            ? `Save ${pendingCount} change${pendingCount === 1 ? '' : 's'}`
                                            : 'Done editing'
                                    }
                                    color="blue"
                                    disabled={saving}
                                >
                                    <Check size={13} />
                                </IconButton>
                            </>
                        )}
                    </div>
                }
                footer={
                    <DataSheetPagination
                        currentPage={page}
                        totalPages={totalPages}
                        pageSize={pageSize}
                        totalItems={total}
                        pageSizeOptions={[20, 50, 100]}
                        onPageChange={setPage}
                        onPageSizeChange={setPageSize}
                    />
                }
                emptyState={
                    <Text
                        size="sm"
                        ta="center"
                        py="xl"
                        style={{ color: tokens.colors.textSecondary }}
                    >
                        No {entityType.toLowerCase()}s found
                    </Text>
                }
                maxHeight={600}
            />

            {saveError && (
                <div
                    style={{
                        margin: '8px 0',
                        padding: '8px 12px',
                        fontSize: 12,
                        color: 'var(--mantine-color-red-7)',
                        background: 'var(--mantine-color-red-0)',
                        border: '1px solid var(--mantine-color-red-3)',
                        borderRadius: 4,
                    }}
                >
                    {saveError}
                </div>
            )}

            {/* Edit mode confirmation */}
            <Modal
                opened={editConfirmOpen}
                onClose={() => setEditConfirmOpen(false)}
                title="Edit entity records"
                size="sm"
                centered
            >
                <Stack gap="md">
                    <Group gap="xs" align="flex-start">
                        <Warning
                            size={20}
                            color="var(--mantine-color-yellow-6)"
                            weight="fill"
                        />
                        <Text size="sm">
                            Edited cells will be highlighted in yellow. Changes
                            are only saved to the database when you click the{' '}
                            <strong>✓</strong> button.
                        </Text>
                    </Group>
                    <Group justify="flex-end">
                        <button
                            type="button"
                            onClick={() => setEditConfirmOpen(false)}
                            style={{
                                padding: '5px 14px',
                                fontSize: 12,
                                border: `1px solid ${tokens.colors.border}`,
                                borderRadius: 4,
                                backgroundColor: tokens.colors.surface,
                                cursor: 'pointer',
                                height: 30,
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setEditConfirmOpen(false);
                                setEditMode(true);
                            }}
                            style={{
                                padding: '5px 14px',
                                fontSize: 12,
                                fontWeight: 500,
                                border: 'none',
                                borderRadius: 4,
                                backgroundColor: tokens.colors.activeTab,
                                color: tokens.colors.surface,
                                cursor: 'pointer',
                                height: 30,
                            }}
                        >
                            Start editing
                        </button>
                    </Group>
                </Stack>
            </Modal>

            {/* No custom columns where an attribute could only key on half the row. */}
            {attributeEntityType(config.entityType) && (
                <AddColumnModal
                    opened={addColumnOpen}
                    onClose={() => setAddColumnOpen(false)}
                    entityType={attributeEntityType(config.entityType)!}
                    onConfirm={handleAddColumnConfirm}
                />
            )}

            {canAddProduct && (
                <>
                    <AddProductModal
                        opened={addProductOpen}
                        onClose={() => setAddProductOpen(false)}
                        onCreated={reload}
                    />
                    <ImportCsvModal
                        opened={importCsvOpen}
                        onClose={() => setImportCsvOpen(false)}
                        onImported={reload}
                        entityNoun="product"
                        columns={PRODUCT_UPSERT_CSV_COLUMNS}
                        requiredColumns={PRODUCT_UPSERT_REQUIRED_COLUMNS}
                        keyLabel="Product ID"
                        upload={bulkUpsertProducts}
                        mode="upsert"
                    />
                </>
            )}

            {canAddVendor && (
                <AddVendorModal
                    opened={addVendorOpen}
                    onClose={() => setAddVendorOpen(false)}
                    onCreated={reload}
                />
            )}

            {historyRow ? (
                <PriceHistoryModal
                    opened
                    onClose={() => setHistoryRow(null)}
                    title={`Price history — ${String(historyRow.productName ?? historyRow.materialName ?? historyRow.itemName ?? historyRow.productId ?? historyRow.materialId ?? historyRow.itemId ?? '')} · ${String(historyRow.vendorId ?? '')}`}
                    currentPrice={(historyRow.price ?? historyRow.cost ?? null) as string | null}
                    currency={(historyRow.currency ?? null) as string | null}
                    priceSince={(historyRow.priceSince ?? null) as string | null}
                    historyText={(historyRow.priceHistory ?? null) as string | null}
                />
            ) : null}

            {termsVendor ? (
                <VendorTermsModal
                    vendor={termsVendor}
                    opened
                    onClose={() => setTermsVendor(null)}
                    onSaved={() => void refetchVendorTerms()}
                />
            ) : null}

            {canImportPrices && (
                <AddVendorPriceModal opened={addPriceOpen} onClose={() => setAddPriceOpen(false)} onCreated={reload} />
            )}

            {canAddMapping && (
                <AddVendorMappingModal
                    opened={addMappingOpen}
                    onClose={() => setAddMappingOpen(false)}
                    onCreated={reload}
                    kind={entityType === 'VENDOR_MATERIAL_MAPPING' ? 'material' : 'product'}
                />
            )}

            {canRemoveMapping && (
                <Modal
                    opened={removeTarget !== null}
                    onClose={() => setRemoveTarget(null)}
                    title="Remove mapping"
                    size="sm"
                    centered
                >
                    <Stack gap="md">
                        <Text size="sm">
                            Stop saying that <strong>{String(removeTarget?.vendorId ?? '')}</strong>{' '}
                            supplies <strong>{String(removeTarget?.materialName || removeTarget?.materialId || '')}</strong>?
                            Receipts and orders already recorded against it are not touched. To
                            change the vendor, remove this mapping and add one for the right vendor.
                        </Text>
                        {removeError && (
                            <Text size="sm" c="red">
                                {removeError}
                            </Text>
                        )}
                        <Group justify="flex-end">
                            <button
                                type="button"
                                onClick={() => setRemoveTarget(null)}
                                style={{
                                    padding: '5px 14px',
                                    fontSize: 12,
                                    border: `1px solid ${tokens.colors.border}`,
                                    borderRadius: 4,
                                    backgroundColor: tokens.colors.surface,
                                    cursor: 'pointer',
                                    height: 30,
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={removing}
                                onClick={() => void handleRemoveMapping()}
                                style={{
                                    padding: '5px 14px',
                                    fontSize: 12,
                                    fontWeight: 500,
                                    border: 'none',
                                    borderRadius: 4,
                                    backgroundColor: 'var(--mantine-color-red-6)',
                                    color: tokens.colors.surface,
                                    cursor: removing ? 'wait' : 'pointer',
                                    height: 30,
                                }}
                            >
                                {removing ? 'Removing…' : 'Remove'}
                            </button>
                        </Group>
                    </Stack>
                </Modal>
            )}

            {canAddMapping && (
                <ImportCsvModal
                    opened={importCsvOpen}
                    onClose={() => setImportCsvOpen(false)}
                    onImported={reload}
                    entityNoun="mapping"
                    mode="upsert"
                    {...(entityType === 'VENDOR_MATERIAL_MAPPING'
                        ? {
                              columns: VENDOR_MATERIAL_MAPPING_CSV_COLUMNS,
                              requiredColumns: VENDOR_MATERIAL_MAPPING_CSV_REQUIRED_COLUMNS,
                              keyLabel: 'Material',
                              upload: bulkUpsertVendorMaterialMappings,
                          }
                        : {
                              columns: VENDOR_MAPPING_CSV_COLUMNS,
                              requiredColumns: VENDOR_MAPPING_CSV_REQUIRED_COLUMNS,
                              keyLabel: 'Product',
                              upload: bulkUpsertVendorMappings,
                          })}
                />
            )}

            {canAddMaterial && (
                <>
                    <AddMaterialModal
                        opened={addMaterialOpen}
                        onClose={() => setAddMaterialOpen(false)}
                        onCreated={reload}
                    />
                    <ImportCsvModal
                        opened={importCsvOpen}
                        onClose={() => setImportCsvOpen(false)}
                        onImported={reload}
                        entityNoun="material"
                        columns={MATERIAL_UPSERT_CSV_COLUMNS}
                        requiredColumns={MATERIAL_UPSERT_REQUIRED_COLUMNS}
                        keyLabel="Material ID"
                        upload={bulkUpsertMaterials}
                        mode="upsert"
                    />
                </>
            )}

            {/* Suppressed for mappings: the generic bulk update identifies a row by
                its first column, and these tables are keyed by two. Their Import CSV
                above does the same job with a real two-column key. */}
            {!isMappingEntity(entityType) && (
            <BulkUploadModal
                opened={bulkUploadOpen}
                onClose={() => setBulkUploadOpen(false)}
                entityType={entityType}
                columns={apiColumns}
                sampleEntityKey={sampleEntityKey}
                onUploaded={() => {
                    setBulkUploadOpen(false);
                    reload();
                }}
            />
            )}
        </>
    );
};

export default EntityMasterTab;
